import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase.js';
import { useLocale } from '../hooks/useLocale.jsx';
import { ReferralService, REFERRAL_CODE_RE } from '../services/referral-service.js';
import { shareLink } from '../services/share-service.js';
import { isNativeApp, isIOS, publicOrigin } from '../services/platform-service.js';
import { useCreditConfig } from '../services/credit-config.js';

function buildShareUrl(code) {
  if (!code) return '';
  return `${publicOrigin()}/?ref=${encodeURIComponent(code)}`;
}

export function Invite({ user, onSignInRequest }) {
  const { t } = useLocale();
  // 양쪽 다 같은 금액 — "너도 100, 나도 100" 이 설명이 짧고 공유할 때 설득력 있다.
  const INVITEE_BONUS = useCreditConfig().inviteReward;
  const INVITER_BONUS = INVITEE_BONUS;
  const [referralCode, setReferralCode] = useState(null);
  const [copyState, setCopyState] = useState('idle');
  // ?ref= 로 들어왔으면 폼을 미리 채운다 — 사용자가 코드를 옮겨 적을 이유가
  // 없고, 무엇보다 이미 로그인한 세션에서는 자동 회수가 안 돌기 때문에
  // (auth-service.js 의 initializeCredits 는 로그인 직후 한 번) 이 폼이 유일한
  // 회수 경로다.
  const [promoCode, setPromoCode] = useState(() => ReferralService.getPendingReferral() || '');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [promoError, setPromoError] = useState(null);

  const isLoggedIn = user && !user.isAnonymous;

  useEffect(() => {
    if (!isLoggedIn) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      setReferralCode(snap.data()?.referralCode || null);
    }, (err) => {
      console.warn('Invite: referralCode snapshot error:', err);
    });
    return unsub;
  }, [isLoggedIn, user?.uid]);

  if (!isLoggedIn) {
    return (
      <main className="main">
        <div className="empty-state">
          <i className="material-symbols-rounded empty-icon">group_add</i>
          <h2>{t('signInToSee')}</h2>
          <p>{t('inviteSignInHint')}</p>
          <button className="btn btn-primary" onClick={() => onSignInRequest?.()}>
            <i className="material-symbols-rounded">login</i>
            {t('signIn')}
          </button>
        </div>
      </main>
    );
  }

  const shareUrl = buildShareUrl(referralCode);

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1800);
    } catch {
      setCopyState('failed');
    }
  };

  const handleShare = async () => {
    if (!shareUrl) { handleCopy(); return; }
    try {
      const shared = await shareLink({
        title: t('appName'),
        text: t('inviteShareText', { n: INVITEE_BONUS }),
        url: shareUrl,
      });
      // shareLink returns false when it falls back to copying — show the
      // same "copied" UI as the explicit copy button does.
      if (!shared) handleCopy();
    } catch {
      // User cancelled — nothing to do.
    }
  };

  // 코드 입력칸 하나로 프로모 + 초대 코드를 둘 다 받는다. 형식이 ARCH-/VODA-
  // 면 초대 코드로 보고 redeemReferral 로 보낸다.
  //
  // 초대 코드까지 여기서 받는 이유: 초대 링크를 브라우저에서 열고 앱을 설치한
  // 사람은 코드가 브라우저 localStorage 에 남아 앱으로 안 넘어온다. 그 경우
  // 여기에 직접 넣는 게 유일한 회수 경로다 (Landing 이 코드를 보여준다).
  const handleRedeemCode = async (e) => {
    e.preventDefault();
    if (!promoCode.trim() || promoLoading) return;
    setPromoLoading(true);
    setPromoError(null);
    setPromoResult(null);
    const entered = promoCode.trim().toUpperCase();
    try {
      if (REFERRAL_CODE_RE.test(entered)) {
        const r = await ReferralService.redeemCode(entered);
        // ALREADY = 이미 다른 초대를 사용함. 성공처럼 보이면 안 된다.
        // 성공이든 ALREADY 든 그 코드로는 더 할 게 없다 — stash 를 비워
        // /invite 를 다시 열 때 또 채워지지 않게 한다.
        ReferralService.clearPendingReferral();
        if (r?.status === 'ALREADY') {
          setPromoError('ALREADY_REDEEMED');
        } else {
          setPromoResult({ status: 'OK', creditsGranted: r?.inviteeCredits ?? INVITEE_BONUS });
          setPromoCode('');
        }
      } else {
        const r = await ReferralService.redeemPromo(entered);
        // creditsGranted 는 구버전 앱용 디자인 개수 — 2.3.0 은 amountGranted (크레딧).
        setPromoResult({ ...r, creditsGranted: r?.amountGranted ?? r?.creditsGranted });
        setPromoCode('');
      }
    } catch (err) {
      setPromoError(err.code || 'REDEEM_FAILED');
    } finally {
      setPromoLoading(false);
    }
  };

  const promoErrorMessage = (code) => {
    switch (code) {
      case 'CODE_NOT_FOUND': return t('errPromoInvalid');
      case 'EXPIRED': return t('errPromoExpired');
      case 'MAX_USES_REACHED': return t('errPromoMaxed');
      case 'ALREADY_REDEEMED': return t('errPromoUsed');
      case 'USER_NOT_INITIALIZED': return t('errPromoUserInit');
      case 'CODE_HAS_NO_CREDITS': return t('errPromoInvalid');
      case 'INVALID_CODE': return t('errPromoInvalid');
      // 초대 코드 경로 (redeemReferral) 에서만 나오는 것.
      case 'SELF_REFERRAL': return t('errRefSelf');
      default: return t('errPromoGeneric');
    }
  };

  return (
    <main className="main invite-page">
      <h1 className="page-title">{t('inviteTitle')}</h1>
      <p className="invite-subtitle">
        {t('inviteSubtitle', { inviter: INVITER_BONUS, invitee: INVITEE_BONUS })}
      </p>

      <section className="invite-card">
        <h2 className="invite-card-title">{t('yourReferralCode')}</h2>
        {referralCode ? (
          <>
            <div className="invite-code-row">
              <code className="invite-code">{referralCode}</code>
            </div>
            <div className="invite-link-row">
              <input
                className="invite-link-input"
                value={shareUrl}
                readOnly
                onFocus={(e) => e.target.select()}
              />
              <button className="btn btn-secondary" onClick={handleCopy}>
                <i className="material-symbols-rounded">
                  {copyState === 'copied' ? 'check' : 'content_copy'}
                </i>
                {copyState === 'copied' ? t('copied') : t('copyLink')}
              </button>
            </div>
            {(isNativeApp() || (typeof navigator !== 'undefined' && navigator.share)) && (
              <button className="btn btn-primary invite-share-btn" onClick={handleShare}>
                <i className="material-symbols-rounded">share</i>
                {t('shareLink')}
              </button>
            )}
            <p className="invite-hint">{t('inviteLinkHint', { n: INVITEE_BONUS })}</p>
          </>
        ) : (
          <p className="invite-hint">{t('inviteCodeLoading')}</p>
        )}
      </section>

      {/* iOS 앱엔 코드 입력칸을 두지 않는다 — 2.4.0 (크레딧 팩 첫 제출) 이 Guideline 3.1.1
         "IAP 외의 수단(코드)으로 디지털 콘텐츠를 연다" 로 걸렸다 (2026-10-31). 초대 링크로 연
         가입은 로그인 직후 자동 회수(redeemPendingReferral)라 그대로 된다.
         Android 는 유지 — 모바일 랜딩(Landing.jsx)이 초대 코드를 보여주고 "설치 후 여기 입력" 으로
         보내는, 새 사용자 초대 보상의 사실상 유일한 회수 경로다. */}
      {!(isNativeApp() && isIOS()) && (
      <section className="invite-card">
        <h2 className="invite-card-title">{t('redeemTitle')}</h2>
        <p className="invite-card-desc">{t('redeemDesc')}</p>
        <form className="promo-form" onSubmit={handleRedeemCode}>
          <input
            type="text"
            className="promo-input"
            placeholder={t('promoPlaceholder')}
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            maxLength={40}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!promoCode.trim() || promoLoading}
          >
            {promoLoading ? t('loading') : t('promoRedeem')}
          </button>
        </form>
        {promoResult?.status === 'OK' && (
          <p className="promo-success">
            <i className="material-symbols-rounded">check_circle</i>
            {t('promoSuccess', { n: promoResult.creditsGranted })}
          </p>
        )}
        {promoError && (
          <p className="promo-error">
            <i className="material-symbols-rounded">error_outline</i>
            {promoErrorMessage(promoError)}
          </p>
        )}
      </section>
      )}
    </main>
  );
}
